import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  debounce,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Checkbox,
  ListItemText,
  Grid,
  Tooltip,
  Box,
  FormControlLabel,
  Menu,
  InputAdornment,
  Alert,
  useTheme,
  useMediaQuery,
  GlobalStyles,
  Slider,
  Drawer,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import "rsuite/dist/rsuite.min.css"; // Import styles once in your project
import { DateRangePicker } from "rsuite";
import RsuiteDateRangePickerWrapper from "components/common/global-style/GlobalStyleDatepicker";
import { useCallback, useEffect, useMemo, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import "react-calendar-datetime-picker/dist/style.css";

import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

import "./FilterPanel.css";
import { AccessTime } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import OverclockedGauge from "components/common/customIcon/overClockedGaugeIcon";

import { Customer, view } from "../models/workOrderTypes";
import {
  DEFAULT_SEARCH_INFO_ALERT,
  DELIVERY_FROM_DATE,
  DELIVERY_TO_DATE,
  FILTER_CLOCKED_HOURS,
  FILTER_OVER_CLOCKED_HOURS,
  FROMDATE,
  GENERIC_SEARCH,
  INCLUDE_FINALIZED,
  PICKUP_FROM_DATE,
  PICKUP_TO_DATE,
  SCHEDULE_FROM_DATE,
  SCHEDULE_TO_DATE,
  SELECTED_WO_CUSTOMERS,
  SELECTED_WO_LOCS,
  SELECTED_WO_statuses,
  SELECTED_WO_TECHS,
  SELECTED_WO_SALES,
  SELECTED_WO_PRIORITIES,
  TODATE,
  USER_IDENTITY,
  VIEW,
  BOARD_ZOOM,
  COLLAPSE_WORK_ORDERS,
} from "../../common/constants/filterParamConstants";
import { useWorkOrderStore } from "../../store/zustandWorkOrderStore";
import { CustomerAPI } from "../api/customerApi";

import WorkOrderDialogContainer from "components/workOrder/WorkOrderDialogContainerMain";
import {
  commonFieldStyle,
  searchFieldPlaceholderStyle,
  filterFieldGridProps,
  searchFieldGridProps,
  templateSelectorGridProps,
  menuItemStyle,
  selectAllMenuItemStyle,
  selectAllCheckboxStyle,
  selectAllLabelStyle,
  dropdownMenuProps,
  actionIconButton,
  primaryActionButton,
  buttonGroupContainer,
  zoomControlContainer,
  formControlStyle,
  drawerSectionStyle,
  drawerLabelStyle,
} from "./FilterPanelStyles";
import { FilterPanelProps } from "./types/FilterPanelTypes";
import FilterTemplateSelector from "./components/FilterTemplateSelector";
import MultiSelectFilterTooltip from "./components/MultiSelectFilterTooltip";
import { useFilterTemplates } from "./hooks/useFilterTemplates";
import { SavedFilterValues } from "./types/FilterTemplateTypes";
import {
  applySavedFiltersToStore,
  DEFAULT_SAVED_FILTER_VALUES,
  mapSavedFiltersToLocalState,
  persistSavedFiltersToLocalStorage,
} from "./utils/filterTemplateUtils";
import packageJson from "../../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "components/authentication/models/authTypes";
import localStorageHelper from "../../../app/localStorage";
import React, { useRef } from "react";
import {
  fadeInUp,
  scaleIn,
} from "components/common/global-style/GlobalStyling";

const FilterPanel: React.FC<FilterPanelProps> = ({
  selectedView,
  onSelectedViewChange,
  initialLoadComplete = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user"),
  );

  const safeUser = useMemo(
    () => ({
      userNameAndCompany: user?.userNameAndCompany ?? "",
      company: user?.company ?? "",
      userName: user?.userName ?? "",
    }),
    [user],
  );

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showDialog, setShowDialog] = useState(false);

  //Zustand
  const { filters, setFilters } = useWorkOrderStore();
  const [userName] = useState<string | "">(
    localStorage.getItem("username") || "",
  );
  const {
    templates,
    activeTemplateId,
    saveTemplate,
    updateTemplate,
    renameTemplate,
    deleteTemplate,
    loadTemplate,
    clearActiveTemplate,
  } = useFilterTemplates();

  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>(() => {
    const customers = localStorage.getItem(
      `${SELECTED_WO_CUSTOMERS}_${userName}`,
    );
    return customers ? JSON.parse(customers) : [];
  });

  // Sync selectedCustomers with filters.woCustomers when filters change
  useEffect(() => {
    if (filters.woCustomers && filters.woCustomers.length > 0) {
      // Convert customer IDs to customer objects if needed
      // This assumes we have access to the full customer list
      // For now, we'll just ensure the IDs match
      const currentCustomerIds = selectedCustomers.map((c) => c.identity);
      const filterCustomerIds = filters.woCustomers;

      if (!arraysEqual(currentCustomerIds, filterCustomerIds)) {
        // If the IDs don't match, we need to update selectedCustomers
        // This is a simplified approach - in a real scenario, you'd need to fetch customer details
        // console.log(
        //   "Customer IDs mismatch detected, but full sync requires customer data"
        // );
      }
    } else if (!filters.woCustomers || filters.woCustomers.length === 0) {
      // Clear selectedCustomers if filters.woCustomers is empty or undefined
      if (selectedCustomers.length > 0) {
        setSelectedCustomers([]);
      }
    }
  }, [filters.woCustomers, selectedCustomers]);

  // Helper function to compare arrays
  const arraysEqual = (a: number[], b: number[]) => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  };

  const stringArraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  };

  //Zustand
  const {
    fetchWorkOrders,
    woStatuses,
    techniciansForFilters,
    salesPersons,
    locations,
    woPriorityList,
    loading,
  } = useWorkOrderStore();

  // Add ref to track if component has initialized to prevent initial fetch
  const hasInitialized = useRef(false);

  // Add event listener for work order refresh (moved from individual dashboards)
  useEffect(() => {
    const handleRefreshWorkOrders = () => {
      // Refresh work orders data without page reload
      if (fetchWorkOrders) {
        // Create a proper Filter object with required view property
        const currentFilters = {
          view: filters?.view || 0,
          ...filters,
        };
        //console.log("fetched from FilterPanel refresh event");
        fetchWorkOrders(currentFilters);
      }
    };

    window.addEventListener("refreshWorkOrders", handleRefreshWorkOrders);

    return () => {
      window.removeEventListener("refreshWorkOrders", handleRefreshWorkOrders);
    };
  }, [fetchWorkOrders, filters]);

  const [wostatus, setWostatus] = useState<string[]>(() => {
    const statuses = localStorage.getItem(
      `${SELECTED_WO_statuses}_${userName}`,
    );
    return statuses ? statuses.split(",").map((s) => s.trim()) : ["0"];
  });

  const [woTechnician, setWoTechnician] = useState<string[]>(() => {
    const techs = localStorage.getItem(`${SELECTED_WO_TECHS}_${userName}`);
    return techs ? techs.split(",").map((s) => s.trim()) : ["0"];
  });

  const [woSalesPerson, setWoSalesPerson] = useState<string[]>(() => {
    const sales = localStorage.getItem(`${SELECTED_WO_SALES}_${userName}`);
    return sales ? sales.split(",").map((s) => s.trim()) : ["0"];
  });

  const [woLocation, setWoLocation] = useState<string[]>(() => {
    const locs = localStorage.getItem(`${SELECTED_WO_LOCS}_${userName}`);
    return locs ? locs.split(",").map((s) => s.trim()) : ["0"];
  });

  const [woPriority, setWoPriority] = useState<string[]>(() => {
    const priorities = localStorage.getItem(
      `${SELECTED_WO_PRIORITIES}_${userName}`,
    );
    return priorities ? priorities.split(",").map((s) => s.trim()) : ["0"];
  });

  const [genSearchVal, setGenSearchVal] = useState<string | null>(() => {
    const searchToken = localStorage.getItem(`${GENERIC_SEARCH}_${userName}`);
    return searchToken ? searchToken : "";
  });

  // Sync genSearchVal with filters.searchString when filters change
  useEffect(() => {
    if (
      filters.searchString !== undefined &&
      filters.searchString !== genSearchVal
    ) {
      setGenSearchVal(filters.searchString);
    }
  }, [filters.searchString, genSearchVal]);

  // Sync woLocation with filters.woLocations when filters change
  useEffect(() => {
    if (
      filters.woLocations !== undefined &&
      !stringArraysEqual(filters.woLocations || [], woLocation)
    ) {
      setWoLocation(filters.woLocations || ["0"]);
    }
  }, [filters.woLocations, woLocation]);

  // Sync wostatus with filters.woStatuses when filters change
  useEffect(() => {
    if (
      filters.woStatuses !== undefined &&
      !stringArraysEqual(filters.woStatuses || [], wostatus)
    ) {
      setWostatus(filters.woStatuses || ["0"]);
    }
  }, [filters.woStatuses, wostatus]);

  // Sync woTechnician with filters.woTechs when filters change
  useEffect(() => {
    if (
      filters.woTechs !== undefined &&
      !stringArraysEqual(filters.woTechs || [], woTechnician)
    ) {
      setWoTechnician(filters.woTechs || ["0"]);
    }
  }, [filters.woTechs, woTechnician]);

  useEffect(() => {
    if (
      filters.woSalesPersons !== undefined &&
      !stringArraysEqual(filters.woSalesPersons || [], woSalesPerson)
    ) {
      setWoSalesPerson(filters.woSalesPersons || ["0"]);
    }
  }, [filters.woSalesPersons, woSalesPerson]);

  useEffect(() => {
    if (
      filters.woPriorities !== undefined &&
      !stringArraysEqual(filters.woPriorities || [], woPriority)
    ) {
      setWoPriority(filters.woPriorities || ["0"]);
    }
  }, [filters.woPriorities, woPriority]);

  const [clockedHourFilter, setClockedHourFilter] = useState<boolean>(
    localStorage.getItem(`${FILTER_CLOCKED_HOURS}_${userName}`) === "true"
      ? true
      : false,
  );

  const [overClockedHourFilter, setOverClockedHourFilter] = useState<boolean>(
    localStorage.getItem(`${FILTER_OVER_CLOCKED_HOURS}_${userName}`) === "true"
      ? true
      : false,
  );

  const [includeFinalizedFilter, setIncludeFinalizedFilter] = useState<boolean>(
    localStorage.getItem(`${INCLUDE_FINALIZED}_${userName}`) === "true",
  );

  const [collapseWorkOrders, setCollapseWorkOrders] = useState<boolean>(
    localStorage.getItem(`${COLLAPSE_WORK_ORDERS}_${userName}`) === "true",
  );

  // Board zoom level - stored in localStorage, default 1.0 (100%)
  const [boardZoom, setBoardZoom] = useState<number>(() => {
    const savedZoom = localStorage.getItem(`${BOARD_ZOOM}_${userName}`);
    return savedZoom ? parseFloat(savedZoom) : 1.0;
  });

  // Initialize zoom and collapse from filters (loaded from localStorage in Zustand store)
  useEffect(() => {
    // Sync boardZoom from filters (initialized from localStorage in store)
    if (filters.boardZoom !== undefined) {
      if (filters.boardZoom !== boardZoom) {
        setBoardZoom(filters.boardZoom);
      }
    } else {
      // If not in filters yet, set it from localStorage value
      setFilters(BOARD_ZOOM, boardZoom);
    }
    // Sync collapseWorkOrders from filters (initialized from localStorage in store)
    if (typeof filters.collapseWorkOrders === "boolean") {
      if (filters.collapseWorkOrders !== collapseWorkOrders) {
        setCollapseWorkOrders(filters.collapseWorkOrders);
      }
    } else {
      // If not in filters yet, set it from localStorage value
      setFilters(COLLAPSE_WORK_ORDERS, collapseWorkOrders);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync boardZoom with filters
  useEffect(() => {
    if (filters.boardZoom !== undefined && filters.boardZoom !== boardZoom) {
      setBoardZoom(filters.boardZoom);
    }
  }, [filters.boardZoom, boardZoom]);

  const handleZoomChange = (newZoom: number) => {
    setBoardZoom(newZoom);
    localStorage.setItem(`${BOARD_ZOOM}_${userName}`, newZoom.toString());
    setFilters(BOARD_ZOOM, newZoom);
  };

  const handleZoomIn = () => {
    const newZoom = Math.min(boardZoom + 0.1, 1.3);
    handleZoomChange(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(boardZoom - 0.1, 0.7);
    handleZoomChange(newZoom);
  };

  const [showAlert, setShowAlert] = useState<boolean>(
    localStorage.getItem(`${DEFAULT_SEARCH_INFO_ALERT}_${userName}`) === "true"
      ? true
      : false,
  );
  const isValidDate = (date: any) => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  useEffect(() => {
    if (
      typeof filters.includeFinalized === "boolean" &&
      filters.includeFinalized !== includeFinalizedFilter
    ) {
      setIncludeFinalizedFilter(filters.includeFinalized);
    }
  }, [filters.includeFinalized, includeFinalizedFilter]);

  useEffect(() => {
    if (
      typeof filters.collapseWorkOrders === "boolean" &&
      filters.collapseWorkOrders !== collapseWorkOrders
    ) {
      setCollapseWorkOrders(filters.collapseWorkOrders);
    }
  }, [filters.collapseWorkOrders, collapseWorkOrders]);

  const handleDateChange =
    (setDate: React.Dispatch<React.SetStateAction<[Date, Date] | null>>) =>
    (value: [Date | null, Date | null] | null) => {
      if (!value || !Array.isArray(value) || value.length !== 2) {
        setDate(null);
        return;
      }

      const [start, end] = value;

      // Only set state when both dates exist and are valid
      if (!start || !end || !isValidDate(start) || !isValidDate(end)) {
        setDate(null);
        return;
      }

      // Ensure second date is after the first date
      if (end.getTime() >= start.getTime()) {
        setDate([start, end]);
      }
    };

  const getWoDates = (key: string): [Date, Date] | null => {
    const storedValue = localStorage.getItem(`${key}_${userName}`);
    try {
      if (storedValue) {
        const parsedValue = JSON.parse(storedValue);
        return [new Date(parsedValue.from), new Date(parsedValue.to)];
      }
      return null;
    } catch (error) {
      return null;
    }
  };
  const [woCreationDate, setWoCreationDate] = useState<[Date, Date] | null>(
    () => {
      return getWoDates(FROMDATE);
    },
  );

  const [woDeliveryDate, setWoDeliveryDate] = useState<[Date, Date] | null>(
    () => {
      return getWoDates(DELIVERY_FROM_DATE);
    },
  );

  const [woPickupDate, setWoPickupDate] = useState<[Date, Date] | null>(() => {
    return getWoDates(PICKUP_FROM_DATE);
  });

  const [scheduledDate, setScheduledDate] = useState<[Date, Date] | null>(
    () => {
      return getWoDates(SCHEDULE_FROM_DATE);
    },
  );

  const [loadingCustomer, setLoadingCustomer] = useState(false);

  const [userIdentityVal] = useState<string>(() => {
    const storedUserIdentity = localStorage.getItem(USER_IDENTITY);
    return storedUserIdentity ?? "0";
  });

  // Set userIdentity immediately on component mount
  useEffect(() => {
    if (userIdentityVal) {
      localStorage.setItem(USER_IDENTITY, userIdentityVal);
      const parsedUserIdentity = parseInt(userIdentityVal, 10);
      if (!isNaN(parsedUserIdentity)) {
        setFilters("userIdentity", parsedUserIdentity);
      }
    }
  }, []); // Empty dependency array to run only once on mount

  // Add a ref to keep track of the latest AbortController and request id
  const searchAbortController = useRef<AbortController | null>(null);
  const latestSearchId = useRef(0);

  const [searchLoading, setSearchLoading] = useState(false);

  // Update debouncedFetchWorkOrders to support cancellation and result ordering
  const debouncedFetchWorkOrders = useCallback(
    debounce(async (filters) => {
      // Cancel previous request
      if (searchAbortController.current) {
        searchAbortController.current.abort();
      }
      const controller = new AbortController();
      searchAbortController.current = controller;
      const thisSearchId = ++latestSearchId.current;
      setSearchLoading(true);
      try {
        // Ensure required properties are present in the filters
        const currentView = localStorage.getItem(`${VIEW}_${userName}`)
          ? parseInt(localStorage.getItem(`${VIEW}_${userName}`)!, 10)
          : 0;

        // Exclude UI-only filters (boardZoom and collapseWorkOrders) from API call
        const { boardZoom, collapseWorkOrders, ...apiFilters } = filters;

        const completeFilters = {
          view: currentView,
          ...apiFilters,
          signal: controller.signal,
        };

        //("fetched from filter panel with filters:", completeFilters);
        await fetchWorkOrders(completeFilters);
        // Only update UI if this is the latest search
        if (thisSearchId === latestSearchId.current) {
          setSearchLoading(false);
        }
      } catch (error) {
        if (thisSearchId === latestSearchId.current) {
          setSearchLoading(false);
        }
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Error fetching work orders:", error);
        }
      }
    }, 300), // Slightly increased debounce for better UX
    [],
  );

  useEffect(() => {
    // console.log("FilterPanel useEffect triggered:", {
    //   initialLoadComplete,
    //   hasInitialized: hasInitialized.current,
    //   filters: filters,
    // });

    // Skip if initial load is not complete
    if (!initialLoadComplete) {
      //console.log("Skipping - initial load not complete");
      return;
    }

    // Trigger fetch - this will run on initial load and subsequent filter changes
    // Note: boardZoom and collapseWorkOrders are excluded as they only affect UI
    //console.log("Triggering debouncedFetchWorkOrders");
    debouncedFetchWorkOrders(filters);

    // Mark as initialized after the first render
    if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
  }, [
    filters.customerIdentity,
    filters.searchString,
    filters.woLocations,
    filters.woStatuses,
    filters.woTechs,
    filters.woSalesPersons,
    filters.woPriorities,
    filters.workOrderCreationEndDate,
    filters.workOrderScheduleToDate,

    filters.workOrderDeliveryEndDate,
    filters.workOrderDeliveryToDate,

    filters.workOrderPickupEndDate,
    filters.workOrderPickupToDate,
    filters.userIdentity,
    filters.filterClockedHours,
    filters.filterOverClockedHours,
    filters.includeFinalized,
    filters.woCustomers,
    selectedView,
    // Note: filters.boardZoom and filters.collapseWorkOrders are intentionally excluded
    // as they only affect UI and should not trigger API calls
    initialLoadComplete,
  ]);

  const filterChangeHandler = (event: any) => {
    const { name, value } = event.target;

    setFilters(name, value);
    if (name === GENERIC_SEARCH) {
      setGenSearchVal(value);
      localStorage.setItem(`${GENERIC_SEARCH}_${userName}`, value);
    }
    if (name === VIEW) {
      localStorage.setItem(`${VIEW}_${userName}`, value); // Store view in localStorage
    }
  };
  const showHideWorkOrderPopup = (isPopupToShow: boolean) => {
    setShowDialog(isPopupToShow);
  };

  // Function to fetch customers from the API
  const fetchCustomers = async (query: string) => {
    setLoadingCustomer(true);
    try {
      const response = await CustomerAPI.searchCustomersByName(query);
      setCustomers(response ?? []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoadingCustomer(false);
    }
  };
  // Debounced function to delay the API call
  const debouncedFetchCustomers = useCallback(
    debounce((query) => {
      if (query.length >= 3) {
        fetchCustomers(query);
      } else {
        setCustomers([]);
        setSearchTerm("");
        setFilters(SELECTED_WO_CUSTOMERS, null);
        //refreshQueueBoardHandler(); // Clear the list if query is less than 3 characters
      }
    }, 500),
    [],
  );

  // Save selected customers to local storage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      `${SELECTED_WO_CUSTOMERS}_${userName}`,
      JSON.stringify(selectedCustomers),
    );
  }, [selectedCustomers]);

  // Reset selection
  const resetSelection = () => {
    setSelectedCustomers([]);
    localStorage.removeItem(`${SELECTED_WO_CUSTOMERS}_${userName}`);
    setFilters(SELECTED_WO_CUSTOMERS, null);
  };

  // Handle Customer Selection
  const handleCustomerToggle = (customer: Customer) => {
    setSelectedCustomers((prev) => {
      const exists = prev.some((c) => c.identity === customer.identity);
      const updatedSelection = exists
        ? prev.filter((c) => c.identity !== customer.identity) // Remove
        : [...prev, customer]; // Add
      setFilters(
        SELECTED_WO_CUSTOMERS,
        updatedSelection.map((c) => c.identity),
      );

      localStorage.setItem(
        `${SELECTED_WO_CUSTOMERS}_${userName}`,
        JSON.stringify(updatedSelection),
      );
      return updatedSelection;
    });
  };

  const handleDateUpdate = (
    dateRange: [Date | null, Date | null] | null,
    fromKey: string,
    toKey: string,
  ) => {
    if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
      // If dateRange is null, clear Zustand and localStorage
      setFilters(fromKey, null);
      setFilters(toKey, null);

      localStorage.removeItem(`${fromKey}_${userName}`);
      return; // Exit early if dateRange is invalid
    }

    const [startDate, endDate] = dateRange;

    // Ensure both dates exist and are valid
    if (!startDate || !endDate || !isValidDate(startDate) || !isValidDate(endDate)) {
      return;
    }

    // Ensure endDate is after startDate
    if (endDate.getTime() < startDate.getTime()) {
      return;
    }

    // Dispatch formatted dates to Zustand
    setFilters(fromKey, getFormattedDate(startDate));
    setFilters(toKey, getFormattedDate(endDate));

    // Save to localStorage
    const jsonDate = JSON.stringify({
      from: getFormattedDate(startDate),
      to: getFormattedDate(endDate),
    });

    localStorage.setItem(`${fromKey}_${userName}`, jsonDate);
  };

  useEffect(() => {
    handleDateUpdate(woCreationDate, FROMDATE, TODATE);
  }, [woCreationDate]);

  useEffect(() => {
    handleDateUpdate(scheduledDate, SCHEDULE_FROM_DATE, SCHEDULE_TO_DATE);
  }, [scheduledDate]);

  useEffect(() => {
    handleDateUpdate(woDeliveryDate, DELIVERY_FROM_DATE, DELIVERY_TO_DATE);
  }, [woDeliveryDate]);

  useEffect(() => {
    handleDateUpdate(woPickupDate, PICKUP_FROM_DATE, PICKUP_TO_DATE);
  }, [woPickupDate]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const ToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    logAmplitudeEventWithProps(
      safeUser.userNameAndCompany,
      {
        company: safeUser.company,
        user: safeUser.userName,
      },
      "ToggleSidebar",
      {
        elapsed_ms: "124",
        title: "Work Orders Filters Panel",
        componentName: "FilterPanel.tsx",
        version: packageJson.version,
      },
    );
  };

  const getFormattedDate = (inputDate: Date) => {
    return `${inputDate.getFullYear()}-${String(
      inputDate.getMonth() + 1,
    ).padStart(2, "0")}-${String(inputDate.getDate()).padStart(2, "0")}`;
  };

  function refreshQueueBoardHandler(): void {
    // Note: resetQueueBoards functionality is handled by Zustand store
    debouncedFetchWorkOrders(filters);

    logAmplitudeEventWithProps(
      safeUser.userNameAndCompany,
      {
        company: safeUser.company,
        user: safeUser.userName,
      },
      "refreshQueueBoardHandler",
      {
        elapsed_ms: "124",
        title: "Work Orders Filters Panel",
        componentName: "FilterPanel.tsx",
        version: packageJson.version,
      },
    );
  }

  const getSavableFilterInput = () => ({
    filters,
    view: selectedView,
    selectedCustomers: selectedCustomers.map((customer) => ({
      identity: customer.identity,
      name: customer.name,
    })),
  });

  const applyTemplateFilters = (templateId: string) => {
    const savedFilters = loadTemplate(templateId);
    applySavedFilterValues(savedFilters);

    if (savedFilters.view !== undefined && savedFilters.view !== selectedView) {
      onSelectedViewChange(savedFilters.view ?? 0);
    }
  };

  const applySavedFilterValues = (savedFilters: SavedFilterValues) => {
    persistSavedFiltersToLocalStorage(savedFilters, userName);
    applySavedFiltersToStore(savedFilters, setFilters);

    const localState = mapSavedFiltersToLocalState(savedFilters);
    setGenSearchVal(localState.searchString);
    setWostatus(localState.woStatuses);
    setWoTechnician(localState.woTechs);
    setWoLocation(localState.woLocations);
    setWoSalesPerson(localState.woSalesPersons);
    setWoPriority(localState.woPriorities);
    setSelectedCustomers(localState.selectedCustomers);
    setWoCreationDate(localState.woCreationDate);
    setScheduledDate(localState.scheduledDate);
    setWoDeliveryDate(localState.woDeliveryDate);
    setWoPickupDate(localState.woPickupDate);
    setClockedHourFilter(localState.filterClockedHours);
    setOverClockedHourFilter(localState.filterOverClockedHours);
    setIncludeFinalizedFilter(localState.includeFinalized);
  };

  const resetToDefaultFiltersAndRefresh = () => {
    applySavedFilterValues(DEFAULT_SAVED_FILTER_VALUES);

    if (selectedView !== 0) {
      onSelectedViewChange(0);
    }
  };

  const handleClearTemplateSelection = () => {
    clearActiveTemplate();
    resetToDefaultFiltersAndRefresh();
  };

  const handleSaveTemplate = async (name: string) => {
    await saveTemplate(name, getSavableFilterInput());
  };

  const handleUpdateTemplate = async (templateId: string) => {
    await updateTemplate(templateId, getSavableFilterInput());
  };

  const handleRenameTemplate = async (templateId: string, name: string) => {
    await renameTemplate(templateId, name);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    const wasActive = activeTemplateId === templateId;
    await deleteTemplate(templateId);

    if (wasActive) {
      resetToDefaultFiltersAndRefresh();
    }
  };

  const windowReload = () => {
    clearActiveTemplate();
    resetFiltersCache();
    // Note: resetFilters functionality is handled by Zustand store
    window.location.reload();
  };

  const resetFiltersCache = () => {
    localStorage.setItem(`${SELECTED_WO_LOCS}_${userName}`, "0");
    localStorage.setItem(`${SELECTED_WO_TECHS}_${userName}`, "0");
    localStorage.setItem(`${SELECTED_WO_statuses}_${userName}`, "0");
    localStorage.setItem(`${SELECTED_WO_SALES}_${userName}`, "0");
    localStorage.setItem(`${SELECTED_WO_PRIORITIES}_${userName}`, "0");
    localStorage.removeItem(`${FROMDATE}_${userName}`);
    localStorage.removeItem(`${TODATE}_${userName}`);

    localStorage.removeItem(`${SCHEDULE_FROM_DATE}_${userName}`);
    localStorage.removeItem(`${SCHEDULE_TO_DATE}_${userName}`);

    localStorage.removeItem(`${DELIVERY_FROM_DATE}_${userName}`);
    localStorage.removeItem(`${DELIVERY_TO_DATE}_${userName}`);

    localStorage.removeItem(`${PICKUP_FROM_DATE}_${userName}`);
    localStorage.removeItem(`${PICKUP_TO_DATE}_${userName}`);

    localStorage.setItem(`${VIEW}_${userName}`, "0");
    localStorage.setItem(`${GENERIC_SEARCH}_${userName}`, "");
    localStorage.removeItem(`${USER_IDENTITY}_${userName}`);
    localStorage.setItem(`${FILTER_CLOCKED_HOURS}_${userName}`, "false");
    localStorage.setItem(`${FILTER_OVER_CLOCKED_HOURS}_${userName}`, "false");
    localStorage.setItem(`${SELECTED_WO_CUSTOMERS}_${userName}`, "");
    localStorage.setItem(`${INCLUDE_FINALIZED}_${userName}`, "false");
    localStorage.setItem(`${BOARD_ZOOM}_${userName}`, "1.0"); // Reset zoom to default
    localStorage.setItem(`${COLLAPSE_WORK_ORDERS}_${userName}`, "false"); // Reset collapse to default

    localStorage.setItem(`${DEFAULT_SEARCH_INFO_ALERT}_${userName}`, "true");
  };

  const woStatusChangeHandler = (event: any) => {
    const value = event.target.value;
    if (value.includes("0")) {
      if (!wostatus.includes("0")) {
        setWostatus(["0"]);
        localStorage.setItem(`${SELECTED_WO_statuses}_${userName}`, "0");
        setFilters(SELECTED_WO_statuses, null);
      } else {
        const filteredstatus = value.filter((v: string) => v !== "0");
        setWostatus(filteredstatus);
        localStorage.setItem(
          `${SELECTED_WO_statuses}_${userName}`,
          filteredstatus.join(","),
        );
        setFilters(
          SELECTED_WO_statuses,
          filteredstatus.length ? filteredstatus : null,
        );
      }
    } else {
      if (value.length === 0) {
        setWostatus(["0"]);
        localStorage.setItem(`${SELECTED_WO_statuses}_${userName}`, "0");
        setFilters(SELECTED_WO_statuses, null);
      } else {
        setWostatus(value);
        localStorage.setItem(
          `${SELECTED_WO_statuses}_${userName}`,
          value.join(","),
        );
        setFilters(SELECTED_WO_statuses, value);
      }
    }
    logAmplitudeEventWithProps(
      safeUser.userNameAndCompany,
      {
        company: safeUser.company,
        user: safeUser.userName,
      },
      "woStatusChangeHandler",
      {
        elapsed_ms: "124",
        title: "Filter Panel",
        componentName: "FilterPanel.tsx",
        version: packageJson.version,
      },
    );
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMultiSelectKey, setOpenMultiSelectKey] = useState<
    "woStatus" | "woTechnician" | "woLocation" | "woPriority" | "woSalesPerson" | null
  >(null);

  const createMultiSelectMenuHandlers = (
    key: Exclude<typeof openMultiSelectKey, null>,
  ) => ({
    onOpen: () => setOpenMultiSelectKey(key),
    onClose: () =>
      setOpenMultiSelectKey((current) => (current === key ? null : current)),
  });

  // Customer Event Handlers Start - Open/Close Dropdown
  const customerClickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    logAmplitudeEventWithProps(
      safeUser.userNameAndCompany,
      {
        company: safeUser.company,
        user: safeUser.userName,
      },
      "handleWoSearchByText",
      {
        elapsed_ms: "124",
        title: "Filter Panel",
        componentName: "FilterPanel.tsx",
        version: packageJson.version,
      },
    );
    setSearchTerm(event.target.value);
    debouncedFetchCustomers(event.target.value);
  };
  // Customer Event Handlers End

  const woStatusTooltipItems = useMemo(() => {
    if (wostatus.includes("0")) {
      return null;
    }

    const filteredWorkOrderStatuses = woStatuses.filter((status) =>
      wostatus.includes(String(status.identity)),
    );

    if (filteredWorkOrderStatuses.length <= 2) {
      return null;
    }

    return filteredWorkOrderStatuses.map((status) => status.displayText);
  }, [wostatus, woStatuses]);

  const woTechnicianTooltipItems = useMemo(() => {
    if (woTechnician.includes("0")) {
      return null;
    }

    const filteredTech = techniciansForFilters.filter((technician) =>
      woTechnician.includes(String(technician.identity)),
    );

    if (filteredTech.length <= 2) {
      return null;
    }

    return filteredTech.map((technician) => technician.userName);
  }, [woTechnician, techniciansForFilters]);

  const woLocationTooltipItems = useMemo(() => {
    if (woLocation.includes("0")) {
      return null;
    }

    const filteredWorkOrderLocations = locations.filter((location) =>
      woLocation.includes(String(location.identity)),
    );

    if (filteredWorkOrderLocations.length <= 2) {
      return null;
    }

    return filteredWorkOrderLocations.map((location) => location.displayText);
  }, [woLocation, locations]);

  const woPriorityTooltipItems = useMemo(() => {
    if (woPriority.includes("0")) {
      return null;
    }

    const filteredPriorities = woPriorityList.filter((priority) =>
      woPriority.includes(String(priority.identity)),
    );

    if (filteredPriorities.length <= 2) {
      return null;
    }

    return filteredPriorities.map((priority) => priority.displayText);
  }, [woPriority, woPriorityList]);

  const woSalesPersonTooltipItems = useMemo(() => {
    if (woSalesPerson.includes("0")) {
      return null;
    }

    const filteredSalesPersons = salesPersons.filter((sales) =>
      woSalesPerson.includes(String(sales.identity)),
    );

    if (filteredSalesPersons.length <= 2) {
      return null;
    }

    return filteredSalesPersons.map((sales) => sales.userName);
  }, [woSalesPerson, salesPersons]);

  const customerTooltipItems = useMemo(() => {
    if (selectedCustomers.length <= 2) {
      return null;
    }

    return selectedCustomers.map((customer) => customer.name);
  }, [selectedCustomers]);

  const mergedCustomers = [
    ...selectedCustomers.filter(
      (sc) => !customers.some((c) => c.identity === sc.identity),
    ), // Ensure selected customers appear
    ...customers,
  ];

  const woTechnicianChangeHandler = (event: any) => {
    const value = event.target.value;
    if (value.includes("0")) {
      if (!woTechnician.includes("0")) {
        setWoTechnician(["0"]);
        localStorage.setItem(`${SELECTED_WO_TECHS}_${userName}`, "0");
        setFilters(SELECTED_WO_TECHS, null);
      } else {
        const filteredTechs = value.filter((v: string) => v !== "0");
        setWoTechnician(filteredTechs);
        localStorage.setItem(
          `${SELECTED_WO_TECHS}_${userName}`,
          filteredTechs.join(","),
        );
        setFilters(
          SELECTED_WO_TECHS,
          filteredTechs.length ? filteredTechs : null,
        );
      }
    } else {
      if (value.length === 0) {
        setWoTechnician(["0"]);
        localStorage.setItem(`${SELECTED_WO_TECHS}_${userName}`, "0");
        setFilters(SELECTED_WO_TECHS, null);
      } else {
        setWoTechnician(value);
        localStorage.setItem(
          `${SELECTED_WO_TECHS}_${userName}`,
          value.join(","),
        ); // Ensure correct format
        setFilters(SELECTED_WO_TECHS, value);
      }
    }

    logAmplitudeEventWithProps(
      safeUser.userNameAndCompany,
      {
        company: safeUser.company,
        user: safeUser.userName,
      },
      "woTechnicianChangeHandler",
      {
        elapsed_ms: "124",
        title: "Filter Panel",
        componentName: "FilterPanel.tsx",
        version: packageJson.version,
      },
    );
  };

  const woSalesPersonChangeHandler = (event: any) => {
    const value = event.target.value;
    if (value.includes("0")) {
      if (!woSalesPerson.includes("0")) {
        setWoSalesPerson(["0"]);
        localStorage.setItem(`${SELECTED_WO_SALES}_${userName}`, "0");
        setFilters(SELECTED_WO_SALES, null);
      } else {
        const filteredSales = value.filter((v: string) => v !== "0");
        setWoSalesPerson(filteredSales);
        localStorage.setItem(
          `${SELECTED_WO_SALES}_${userName}`,
          filteredSales.join(","),
        );
        setFilters(
          SELECTED_WO_SALES,
          filteredSales.length ? filteredSales : null,
        );
      }
    } else {
      if (value.length === 0) {
        setWoSalesPerson(["0"]);
        localStorage.setItem(`${SELECTED_WO_SALES}_${userName}`, "0");
        setFilters(SELECTED_WO_SALES, null);
      } else {
        setWoSalesPerson(value);
        localStorage.setItem(
          `${SELECTED_WO_SALES}_${userName}`,
          value.join(","),
        );
        setFilters(SELECTED_WO_SALES, value);
      }
    }

    logAmplitudeEventWithProps(
      safeUser.userNameAndCompany,
      {
        company: safeUser.company,
        user: safeUser.userName,
      },
      "woSalesPersonChangeHandler",
      {
        elapsed_ms: "124",
        title: "Filter Panel",
        componentName: "FilterPanel.tsx",
        version: packageJson.version,
      },
    );
  };

  const includeFinalizedChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.checked;
    setIncludeFinalizedFilter(value);
    localStorage.setItem(`${INCLUDE_FINALIZED}_${userName}`, `${value}`);
    setFilters(INCLUDE_FINALIZED, value);
  };

  const collapseWorkOrdersChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.checked;
    setCollapseWorkOrders(value);
    localStorage.setItem(`${COLLAPSE_WORK_ORDERS}_${userName}`, `${value}`);
    // Update filters for UI sync, but don't trigger API call
    setFilters(COLLAPSE_WORK_ORDERS, value);
  };

  const woLocationChangeHandler = (event: any) => {
    const value = event.target.value;
    if (value.includes("0")) {
      if (!woLocation.includes("0")) {
        setWoLocation(["0"]);
        localStorage.setItem(`${SELECTED_WO_LOCS}_${userName}`, "0");
        setFilters(SELECTED_WO_LOCS, null);
      } else {
        const filteredLocs = value.filter((v: string) => v !== "0");
        setWoLocation(filteredLocs);
        localStorage.setItem(
          `${SELECTED_WO_LOCS}_${userName}`,
          filteredLocs.join(","),
        );
        setFilters(SELECTED_WO_LOCS, filteredLocs.length ? filteredLocs : null);
      }
    } else {
      if (value.length === 0) {
        setWoLocation(["0"]);
        localStorage.setItem(`${SELECTED_WO_LOCS}_${userName}`, "0");
        setFilters(SELECTED_WO_LOCS, null);
      } else {
        setWoLocation(value);
        localStorage.setItem(
          `${SELECTED_WO_LOCS}_${userName}`,
          value.join(","),
        );
        setFilters(SELECTED_WO_LOCS, value);
      }
    }
    logAmplitudeEventWithProps(
      safeUser.userNameAndCompany,
      {
        company: safeUser.company,
        user: safeUser.userName,
      },
      "woLocationChangeHandler",
      {
        elapsed_ms: "124",
        title: "Filter Panel",
        componentName: "FilterPanel.tsx",
        version: packageJson.version,
      },
    );
  };

  const woPriorityChangeHandler = (event: any) => {
    const value = event.target.value;
    if (value.includes("0")) {
      if (!woPriority.includes("0")) {
        setWoPriority(["0"]);
        localStorage.setItem(`${SELECTED_WO_PRIORITIES}_${userName}`, "0");
        setFilters(SELECTED_WO_PRIORITIES, null);
      } else {
        const filteredPriorities = value.filter((v: string) => v !== "0");
        setWoPriority(filteredPriorities);
        localStorage.setItem(
          `${SELECTED_WO_PRIORITIES}_${userName}`,
          filteredPriorities.join(","),
        );
        setFilters(
          SELECTED_WO_PRIORITIES,
          filteredPriorities.length ? filteredPriorities : null,
        );
      }
    } else {
      if (value.length === 0) {
        setWoPriority(["0"]);
        localStorage.setItem(`${SELECTED_WO_PRIORITIES}_${userName}`, "0");
        setFilters(SELECTED_WO_PRIORITIES, null);
      } else {
        setWoPriority(value);
        localStorage.setItem(
          `${SELECTED_WO_PRIORITIES}_${userName}`,
          value.join(","),
        );
        setFilters(SELECTED_WO_PRIORITIES, value);
      }
    }
    logAmplitudeEventWithProps(
      safeUser.userNameAndCompany,
      {
        company: safeUser.company,
        user: safeUser.userName,
      },
      "woPriorityChangeHandler",
      {
        elapsed_ms: "124",
        title: "Filter Panel",
        componentName: "FilterPanel.tsx",
        version: packageJson.version,
      },
    );
  };

  const theme = useTheme();
  const isCompactSearchField = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <Paper
      elevation={0}
      sx={{
        m: 1.5,
        p: 2.5,
        borderRadius: "8px",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 4px 24px rgba(0,0,0,0.3)"
            : "0 4px 24px rgba(0,0,0,0.06)",
        animation: `${fadeInUp} 0.5s ease-out 0.2s both`,
      }}
    >
      <RsuiteDateRangePickerWrapper />
      <GlobalStyles
        styles={(theme) => ({
          "*::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
            borderRadius: "3px",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.15)",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.35)"
                  : "rgba(0, 0, 0, 0.25)",
            },
          },
          "*::-webkit-scrollbar-corner": {
            backgroundColor: "transparent",
          },
          ".rs-picker-popup": {
            zIndex: "1400 !important",
          },
          ".rs-calendar": {
            zIndex: "1400 !important",
          },
          ".rs-picker-daterange-popup": {
            zIndex: "1400 !important",
            maxWidth: "100%",
          },
          ".rs-picker-daterange-menu": {
            zIndex: "1400 !important",
            width: "auto !important",
            minWidth: "540px",
          },
          ".rs-picker-daterange-calendar-group": {
            display: "flex",
            flexDirection: "row",
          },
        })}
      />

      {showAlert && (
        <Alert
          severity="info"
          variant="outlined"
          icon={<InfoIcon sx={{ fontSize: "20px" }} />}
          sx={{
            mb: 2.5,
            borderRadius: "12px",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(41, 182, 246, 0.08)"
                : "rgba(33, 150, 243, 0.06)",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(41, 182, 246, 0.3)"
                : "rgba(33, 150, 243, 0.25)",
            "& .MuiAlert-message": {
              fontSize: "0.875rem",
              color: theme.palette.text.primary,
            },
            "& .MuiAlert-icon": {
              color: theme.palette.info.main,
            },
          }}
          action={
            <IconButton
              aria-label="close"
              size="small"
              onClick={() => {
                setShowAlert(false);
                localStorage.setItem(
                  `${DEFAULT_SEARCH_INFO_ALERT}_${userName}`,
                  "false",
                );
              }}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          Showing results from the last 6 months by default. Adjust work order
          creation date filter to refine your search.
        </Alert>
      )}

      <Grid container spacing={2} alignItems="center">


        {/* Search Work Order Field */}
        <Grid
          item
          {...searchFieldGridProps}
          sx={{ animation: `${scaleIn} 0.4s ease-out 0.3s both` }}
        >
          <TextField
            label={isCompactSearchField ? "Search" : "Search Work Orders"}
            placeholder={isCompactSearchField ? "Search" : "Search Work Orders"}
            sx={{
              ...searchFieldPlaceholderStyle(theme),
              ...formControlStyle(theme),
              "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                maxWidth: "calc(100% - 48px)",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              },
            }}
            variant="outlined"
            size="small"
            fullWidth
            onChange={filterChangeHandler}
            name={GENERIC_SEARCH}
            value={genSearchVal}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SearchIcon fontSize="small" />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* View */}
        <Grid
          item
          {...filterFieldGridProps}
          sx={{ animation: `${scaleIn} 0.4s ease-out 0.35s both` }}
        >
          <FormControl
            size="small"
            variant="outlined"
            fullWidth
            sx={formControlStyle(theme)}
          >
            <InputLabel>View</InputLabel>
            <Select
              sx={commonFieldStyle(theme)}
              onChange={(e) => onSelectedViewChange(e.target.value as number)}
              name={VIEW}
              value={selectedView}
              label="View"
              disabled={view.length === 0}
            >
              {view.map((viewOption) => (
                <MenuItem
                  sx={commonFieldStyle(theme)}
                  key={viewOption.identity}
                  value={viewOption.identity}
                >
                  {viewOption.displayText}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* WO Status */}
        <Grid
          item
          {...filterFieldGridProps}
          sx={{ animation: `${scaleIn} 0.4s ease-out 0.4s both` }}
        >
          <MultiSelectFilterTooltip
            items={woStatusTooltipItems}
            isMenuOpen={openMultiSelectKey === "woStatus"}
          >
            <Box sx={{ width: "100%" }}>
              <FormControl
                size="small"
                variant="outlined"
                fullWidth
                sx={formControlStyle(theme)}
              >
                <InputLabel>WO Status</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  value={wostatus}
                  onChange={woStatusChangeHandler}
                  {...createMultiSelectMenuHandlers("woStatus")}
                  MenuProps={dropdownMenuProps(theme)}
                  renderValue={(selected) => {
                    if (selected.includes("0")) return "All";
                    const filteredWorkOrderStatuses = woStatuses.filter(
                      (status) =>
                        selected.includes(String(status.identity)),
                    );
                    return filteredWorkOrderStatuses.length > 0
                      ? filteredWorkOrderStatuses.length <= 2
                        ? filteredWorkOrderStatuses
                            .map((status) => status.displayText)
                            .join(", ")
                        : `${filteredWorkOrderStatuses.length} selected`
                      : "All";
                  }}
                  sx={commonFieldStyle(theme)}
                  label="WO Status"
                >
                  <MenuItem sx={selectAllMenuItemStyle(theme)}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            woStatuses.length === wostatus.length &&
                            woStatuses.length > 0
                          }
                          indeterminate={
                            woStatuses.length > 0 &&
                            wostatus.length < woStatuses.length
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            if (woStatuses.length === wostatus.length) {
                              woStatusChangeHandler({ target: { value: [] } });
                            } else {
                              const allIds = woStatuses.map((loc) =>
                                String(loc.identity),
                              );
                              woStatusChangeHandler({
                                target: { value: allIds },
                              });
                            }
                          }}
                          sx={selectAllCheckboxStyle}
                          size="small"
                        />
                      }
                      label={
                        woStatuses.length === wostatus.length &&
                        wostatus.length > 0
                          ? "Unselect All"
                          : "Select All"
                      }
                      sx={selectAllLabelStyle}
                    />
                  </MenuItem>

                  {woStatuses.map((status) => (
                    <MenuItem
                      sx={menuItemStyle(theme)}
                      key={status.identity}
                      value={String(status.identity)}
                    >
                      <Checkbox
                        checked={wostatus.includes(String(status.identity))}
                        size="small"
                      />
                      <ListItemText
                        primary={status.displayText}
                        primaryTypographyProps={{ fontSize: "0.875rem" }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </MultiSelectFilterTooltip>
        </Grid>

        {/* Assigned Tech */}
        <Grid
          item
          {...filterFieldGridProps}
          sx={{ animation: `${scaleIn} 0.4s ease-out 0.45s both` }}
        >
          <MultiSelectFilterTooltip
            items={woTechnicianTooltipItems}
            isMenuOpen={openMultiSelectKey === "woTechnician"}
          >
            <Box sx={{ width: "100%" }}>
              <FormControl
                size="small"
                variant="outlined"
                fullWidth
                sx={formControlStyle(theme)}
              >
                <InputLabel id="assigned-tech">Assigned Tech</InputLabel>
                <Select
                  labelId="assigned-tech"
                  MenuProps={dropdownMenuProps(theme)}
                  multiple
                  displayEmpty
                  label="Assigned Tech"
                  value={woTechnician}
                  onChange={woTechnicianChangeHandler}
                  {...createMultiSelectMenuHandlers("woTechnician")}
                  renderValue={(selected) => {
                    if (selected.includes("0")) return "All";
                    const filteredTech = techniciansForFilters.filter(
                      (technician) =>
                        selected.includes(String(technician.identity)),
                    );
                    return filteredTech.length > 0
                      ? filteredTech.length <= 2
                        ? filteredTech.map((t) => t.userName).join(", ")
                        : `${filteredTech.length} selected`
                      : "All";
                  }}
                  sx={commonFieldStyle(theme)}
                >
                  <MenuItem sx={selectAllMenuItemStyle(theme)}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            woTechnician.length ===
                              techniciansForFilters.length &&
                            techniciansForFilters.length > 0
                          }
                          indeterminate={
                            woTechnician.length > 0 &&
                            woTechnician.length < techniciansForFilters.length
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            if (
                              woTechnician.length ===
                              techniciansForFilters.length
                            ) {
                              woTechnicianChangeHandler({
                                target: { value: [] },
                              });
                            } else {
                              const allIds = techniciansForFilters.map((loc) =>
                                String(loc.identity),
                              );
                              woTechnicianChangeHandler({
                                target: { value: allIds },
                              });
                            }
                          }}
                          sx={selectAllCheckboxStyle}
                          size="small"
                        />
                      }
                      label={
                        woTechnician.length === techniciansForFilters.length &&
                        techniciansForFilters.length > 0
                          ? "Unselect All"
                          : "Select All"
                      }
                      sx={selectAllLabelStyle}
                    />
                  </MenuItem>

                  {techniciansForFilters.map((technician) => (
                    <MenuItem
                      sx={menuItemStyle(theme)}
                      key={technician.identity}
                      value={String(technician.identity)}
                    >
                      <Checkbox
                        checked={woTechnician.includes(
                          String(technician.identity),
                        )}
                        size="small"
                      />
                      <ListItemText
                        primary={technician.userName}
                        primaryTypographyProps={{ fontSize: "0.875rem" }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </MultiSelectFilterTooltip>
        </Grid>

        {/* Location */}
        <Grid
          item
          {...filterFieldGridProps}
          sx={{ animation: `${scaleIn} 0.4s ease-out 0.5s both` }}
        >
          <MultiSelectFilterTooltip
            items={woLocationTooltipItems}
            isMenuOpen={openMultiSelectKey === "woLocation"}
          >
            <Box sx={{ width: "100%" }}>
              <FormControl
                size="small"
                variant="outlined"
                fullWidth
                sx={formControlStyle(theme)}
              >
                <InputLabel>Location</InputLabel>
                <Select
                  MenuProps={dropdownMenuProps(theme)}
                  multiple
                  displayEmpty
                  label="Location"
                  value={woLocation}
                  onChange={woLocationChangeHandler}
                  {...createMultiSelectMenuHandlers("woLocation")}
                  renderValue={(selected) => {
                    if (selected.includes("0")) return "All";
                    const filteredWorkOrderLocations = locations.filter(
                      (location) =>
                        selected.includes(String(location.identity)),
                    );
                    return filteredWorkOrderLocations.length > 0
                      ? filteredWorkOrderLocations.length <= 2
                        ? filteredWorkOrderLocations
                            .map((l) => l.displayText)
                            .join(", ")
                        : `${filteredWorkOrderLocations.length} selected`
                      : "All";
                  }}
                  sx={commonFieldStyle(theme)}
                >
                  <MenuItem sx={selectAllMenuItemStyle(theme)}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            woLocation.length === locations.length &&
                            locations.length > 0
                          }
                          indeterminate={
                            woLocation.length > 0 &&
                            woLocation.length < locations.length
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            if (woLocation.length === locations.length) {
                              woLocationChangeHandler({ target: { value: [] } });
                            } else {
                              const allIds = locations.map((loc) =>
                                String(loc.identity),
                              );
                              woLocationChangeHandler({
                                target: { value: allIds },
                              });
                            }
                          }}
                          sx={selectAllCheckboxStyle}
                          size="small"
                        />
                      }
                      label={
                        woLocation.length === locations.length &&
                        locations.length > 0
                          ? "Unselect All"
                          : "Select All"
                      }
                      sx={selectAllLabelStyle}
                    />
                  </MenuItem>

                  {locations.map((location) => (
                    <MenuItem
                      sx={menuItemStyle(theme)}
                      key={location.identity}
                      value={String(location.identity)}
                    >
                      <Checkbox
                        checked={woLocation.includes(String(location.identity))}
                        size="small"
                      />
                      <ListItemText
                        primary={location.displayText}
                        primaryTypographyProps={{ fontSize: "0.875rem" }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </MultiSelectFilterTooltip>
        </Grid>

        {/* Priority */}
        <Grid
          item
          {...filterFieldGridProps}
          sx={{ animation: `${scaleIn} 0.4s ease-out 0.52s both` }}
        >
          <MultiSelectFilterTooltip
            items={woPriorityTooltipItems}
            isMenuOpen={openMultiSelectKey === "woPriority"}
          >
            <Box sx={{ width: "100%" }}>
              <FormControl
                size="small"
                variant="outlined"
                fullWidth
                sx={formControlStyle(theme)}
              >
                <InputLabel>Priority</InputLabel>
                <Select
                  MenuProps={dropdownMenuProps(theme)}
                  multiple
                  displayEmpty
                  label="Priority"
                  value={woPriority}
                  onChange={woPriorityChangeHandler}
                  {...createMultiSelectMenuHandlers("woPriority")}
                  renderValue={(selected) => {
                    if (selected.includes("0")) return "All";
                    const filteredPriorities = woPriorityList.filter(
                      (priority) =>
                        selected.includes(String(priority.identity)),
                    );
                    return filteredPriorities.length > 0
                      ? filteredPriorities.length <= 2
                        ? filteredPriorities
                            .map((p) => p.displayText)
                            .join(", ")
                        : `${filteredPriorities.length} selected`
                      : "All";
                  }}
                  sx={commonFieldStyle(theme)}
                >
                  <MenuItem sx={selectAllMenuItemStyle(theme)}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            woPriority.length === woPriorityList.length &&
                            woPriorityList.length > 0
                          }
                          indeterminate={
                            woPriority.length > 0 &&
                            woPriority.length < woPriorityList.length
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            if (woPriority.length === woPriorityList.length) {
                              woPriorityChangeHandler({
                                target: { value: [] },
                              });
                            } else {
                              const allIds = woPriorityList.map((p) =>
                                String(p.identity),
                              );
                              woPriorityChangeHandler({
                                target: { value: allIds },
                              });
                            }
                          }}
                          sx={selectAllCheckboxStyle}
                          size="small"
                        />
                      }
                      label={
                        woPriority.length === woPriorityList.length &&
                        woPriorityList.length > 0
                          ? "Unselect All"
                          : "Select All"
                      }
                      sx={selectAllLabelStyle}
                    />
                  </MenuItem>

                  {woPriorityList.map((priority) => (
                    <MenuItem
                      sx={menuItemStyle(theme)}
                      key={priority.identity}
                      value={String(priority.identity)}
                    >
                      <Checkbox
                        checked={woPriority.includes(String(priority.identity))}
                        size="small"
                      />
                      <ListItemText
                        primary={priority.displayText}
                        primaryTypographyProps={{ fontSize: "0.875rem" }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </MultiSelectFilterTooltip>
        </Grid>

        {/* Filter Template Selector */}
        <Grid
          item
          {...templateSelectorGridProps(Boolean(activeTemplateId))}
          sx={{ animation: `${scaleIn} 0.4s ease-out 0.25s both` }}
        >
          <FilterTemplateSelector
            templates={templates}
            activeTemplateId={activeTemplateId}
            onTemplateSelect={applyTemplateFilters}
            onSaveTemplate={handleSaveTemplate}
            onUpdateTemplate={handleUpdateTemplate}
            onRenameTemplate={handleRenameTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            onClearSelection={handleClearTemplateSelection}
          />
        </Grid>

        {/* Action Buttons */}
        <Grid
          item
          xs={12}
          md="auto"
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: { xs: 1, md: 0.5, lg: 0.75 },
            ml: { xs: 0, md: 1 },
            mt: { xs: 1.5, md: 0 },
            animation: `${scaleIn} 0.4s ease-out 0.55s both`,
          }}
        >
          {/* Filter Toggle Group */}
          <Box sx={buttonGroupContainer(theme)}>
            <Tooltip title="Filter Clocked Only" arrow placement="top">
              <IconButton
                onClick={() => {
                  setClockedHourFilter(!clockedHourFilter);
                  const value = !clockedHourFilter;
                  localStorage.setItem(
                    `${FILTER_CLOCKED_HOURS}_${userName}`,
                    `${value}`,
                  );
                  setFilters(FILTER_CLOCKED_HOURS, value);
                }}
                sx={{ ...actionIconButton(theme, clockedHourFilter) }}
              >
                <AccessTime sx={{ fontSize: "24px" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Filter Over Clocked WO's" arrow placement="top">
              <IconButton
                onClick={() => {
                  setOverClockedHourFilter(!overClockedHourFilter);
                  const value = !overClockedHourFilter;
                  localStorage.setItem(
                    `${FILTER_OVER_CLOCKED_HOURS}_${userName}`,
                    `${value}`,
                  );
                  setFilters(FILTER_OVER_CLOCKED_HOURS, value);
                }}
                sx={actionIconButton(theme, overClockedHourFilter)}
              >
                <OverclockedGauge isActive={overClockedHourFilter} />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                collapseWorkOrders
                  ? "Expand Work Orders"
                  : "Collapse Work Orders"
              }
              arrow
              placement="top"
            >
              <IconButton
                onClick={() => {
                  const value = !collapseWorkOrders;
                  setCollapseWorkOrders(value);
                  localStorage.setItem(
                    `${COLLAPSE_WORK_ORDERS}_${userName}`,
                    `${value}`,
                  );
                  setFilters(COLLAPSE_WORK_ORDERS, value);
                }}
                sx={actionIconButton(theme, collapseWorkOrders)}
              >
                <img
                  src={collapseWorkOrders ? "/collapseWO.png" : "/expandWO.png"}
                  alt={collapseWorkOrders ? "Collapsed" : "Expanded"}
                  style={{
                    width: "20px",
                    height: "20px",
                    filter: collapseWorkOrders
                      ? "brightness(0) saturate(100%) invert(27%) sepia(91%) saturate(2878%) hue-rotate(212deg) brightness(104%) contrast(97%)"
                      : theme.palette.mode === "dark"
                        ? "invert(1) grayscale(100%) opacity(0.7)"
                        : "grayscale(100%) opacity(0.6)",
                    transition: "filter 0.2s ease",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Quick Actions Group */}
          <Box sx={buttonGroupContainer(theme)}>
            <Tooltip title="Create New Work Order" arrow placement="top">
              <IconButton
                onClick={() => showHideWorkOrderPopup(true)}
                sx={primaryActionButton(theme)}
              >
                <AddCircleOutlineIcon sx={{ fontSize: "22px" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh Board" arrow placement="top">
              <IconButton
                onClick={refreshQueueBoardHandler}
                sx={{
                  ...actionIconButton(theme, false),
                  color: theme.palette.primary.main,
                }}
              >
                <RefreshIcon sx={{ fontSize: "22px" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Reset All Filters" arrow placement="top">
              <IconButton
                onClick={windowReload}
                sx={actionIconButton(theme, false)}
              >
                <FilterAltOffIcon sx={{ fontSize: "22px" }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Zoom Controls */}
          <Box sx={zoomControlContainer(theme)}>
            <Tooltip title="Zoom Out" arrow placement="top">
              <span>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={boardZoom <= 0.7}
                  sx={{
                    ...actionIconButton(theme, false),
                    color:
                      boardZoom <= 0.7
                        ? theme.palette.text.disabled
                        : theme.palette.text.secondary,
                  }}
                >
                  <ZoomOutIcon sx={{ fontSize: "22px" }} />
                </IconButton>
              </span>
            </Tooltip>

            <Box
              sx={{
                width: 60,
                display: "flex",
                alignItems: "center",
                mx: 0.25,
              }}
            >
              <Slider
                value={boardZoom}
                onChange={(_, value) => handleZoomChange(value as number)}
                min={0.7}
                max={1.3}
                step={0.05}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  "& .MuiSlider-thumb": {
                    width: 14,
                    height: 14,
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: `0 0 0 6px ${theme.palette.mode === "dark" ? "rgba(144,202,249,0.16)" : "rgba(25,118,210,0.16)"}`,
                    },
                  },
                  "& .MuiSlider-track": {
                    height: 3,
                  },
                  "& .MuiSlider-rail": {
                    height: 3,
                    opacity: 0.3,
                  },
                }}
              />
            </Box>

            <Tooltip title="Zoom In" arrow placement="top">
              <span>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={boardZoom >= 1.3}
                  sx={{
                    ...actionIconButton(theme, false),
                    color:
                      boardZoom >= 1.3
                        ? theme.palette.text.disabled
                        : theme.palette.text.secondary,
                  }}
                >
                  <ZoomInIcon sx={{ fontSize: "22px" }} />
                </IconButton>
              </span>
            </Tooltip>

            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.primary,
                minWidth: "36px",
                textAlign: "center",
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              {Math.round(boardZoom * 100)}%
            </Typography>
          </Box>

          {/* Advanced Filters Toggle */}
          <Tooltip title="Advanced Filters" arrow placement="top">
            <IconButton
              onClick={ToggleSidebar}
              sx={{
                ...actionIconButton(theme, isSidebarOpen),
                "& svg": {
                  transform: isSidebarOpen ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                },
              }}
            >
              <TuneIcon sx={{ fontSize: "24px" }} />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Filter Sidebar */}
      <Drawer
        anchor="right"
        open={isSidebarOpen}
        onClose={ToggleSidebar}
        PaperProps={{
          sx: {
            width: isDatePickerOpen ? 580 : 360,
            backgroundColor: theme.palette.background.paper,
            transition: "width 0.3s ease-in-out",
            borderTopLeftRadius: 20,
            borderBottomLeftRadius: 20,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 0 40px rgba(0,0,0,0.5)"
                : "0 0 40px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#1976d2",
            p: 2.5,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TuneIcon sx={{ fontSize: "24px", color: "white" }} />
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "white",
                }}
              >
                Advanced Filters
              </Typography>
            </Box>
            <IconButton
              onClick={ToggleSidebar}
              sx={{
                color: "white",
                width: 36,
                height: 36,
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {/* Sales Person Section */}
          <Box sx={drawerSectionStyle(theme)}>
            <Typography sx={drawerLabelStyle(theme)}>Sales Person</Typography>
            <MultiSelectFilterTooltip
              items={woSalesPersonTooltipItems}
              isMenuOpen={openMultiSelectKey === "woSalesPerson"}
            >
              <FormControl
                size="small"
                variant="outlined"
                fullWidth
                sx={formControlStyle(theme)}
              >
                <Select
                  labelId="sales-person-filter-sidebar"
                  MenuProps={dropdownMenuProps(theme)}
                  multiple
                  displayEmpty
                  value={woSalesPerson}
                  onChange={woSalesPersonChangeHandler}
                  {...createMultiSelectMenuHandlers("woSalesPerson")}
                renderValue={(selected) => {
                  if (selected.includes("0")) return "All";
                  const filteredSalesPersons = salesPersons.filter((sales) =>
                    selected.includes(String(sales.identity)),
                  );
                  return filteredSalesPersons.length > 0
                    ? filteredSalesPersons.length <= 2
                      ? filteredSalesPersons.map((s) => s.userName).join(", ")
                      : `${filteredSalesPersons.length} selected`
                    : "All";
                }}
                sx={commonFieldStyle(theme)}
              >
                <MenuItem sx={selectAllMenuItemStyle(theme)}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          woSalesPerson.length === salesPersons.length &&
                          salesPersons.length > 0
                        }
                        indeterminate={
                          woSalesPerson.length > 0 &&
                          woSalesPerson.length < salesPersons.length
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          if (woSalesPerson.length === salesPersons.length) {
                            woSalesPersonChangeHandler({
                              target: { value: [] },
                            });
                          } else {
                            const allIds = salesPersons.map((sales) =>
                              String(sales.identity),
                            );
                            woSalesPersonChangeHandler({
                              target: { value: allIds },
                            });
                          }
                        }}
                        sx={selectAllCheckboxStyle}
                        size="small"
                      />
                    }
                    label={
                      woSalesPerson.length === salesPersons.length &&
                      salesPersons.length > 0
                        ? "Unselect All"
                        : "Select All"
                    }
                    sx={selectAllLabelStyle}
                  />
                </MenuItem>

                {salesPersons.map((sales) => (
                  <MenuItem
                    sx={menuItemStyle(theme)}
                    key={sales.identity}
                    value={String(sales.identity)}
                  >
                    <Checkbox
                      checked={woSalesPerson.includes(String(sales.identity))}
                      size="small"
                    />
                    <ListItemText
                      primary={sales.userName}
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            </MultiSelectFilterTooltip>
          </Box>

          {/* Customer Section */}
          <Box sx={drawerSectionStyle(theme)}>
            <Typography sx={drawerLabelStyle(theme)}>Customers</Typography>
            <FormControl
              size="small"
              variant="outlined"
              fullWidth
              sx={formControlStyle(theme)}
            >
              <MultiSelectFilterTooltip
                items={customerTooltipItems}
                isMenuOpen={Boolean(anchorEl)}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  onClick={customerClickHandler}
                  value={
                    selectedCustomers.length === 0
                      ? "All"
                      : selectedCustomers.length <= 2
                        ? selectedCustomers
                            .map((c) => c.name.slice(0, 5))
                            .join(", ")
                        : `${selectedCustomers.length} customers selected`
                  }
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {selectedCustomers.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              resetSelection();
                            }}
                            sx={{
                              padding: 0.5,
                              "&:hover": {
                                color: theme.palette.error.main,
                              },
                            }}
                          >
                            <ClearIcon sx={{ fontSize: "18px" }} />
                          </IconButton>
                        )}
                        <ArrowDropDownIcon
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      </InputAdornment>
                    ),
                    sx: {
                      cursor: "pointer",
                      borderRadius: "10px",
                    },
                  }}
                  fullWidth
                />
              </MultiSelectFilterTooltip>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    width: 280,
                    maxHeight: 340,
                    overflow: "hidden",
                    borderRadius: "12px",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px rgba(0,0,0,0.4)"
                        : "0 8px 32px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {loadingCustomer ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SearchIcon
                              sx={{
                                fontSize: "20px",
                                color: theme.palette.text.secondary,
                              }}
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ maxHeight: 260, overflowY: "auto", p: 0.5 }}>
                  {mergedCustomers.map((customer) => (
                    <MenuItem
                      key={customer.identity}
                      onClick={() => handleCustomerToggle(customer)}
                      sx={{
                        borderRadius: "8px",
                        mx: 0.5,
                        my: 0.25,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Checkbox
                        checked={selectedCustomers.some(
                          (c) => c.identity === customer.identity,
                        )}
                        size="small"
                      />
                      <ListItemText
                        primary={customer.name}
                        primaryTypographyProps={{ fontSize: "0.875rem" }}
                      />
                    </MenuItem>
                  ))}
                </Box>
              </Menu>
            </FormControl>
          </Box>

          {/* Date Filters Section */}
          <Box sx={{ ...drawerSectionStyle(theme), pt: 2 }}>
            <Typography sx={{ ...drawerLabelStyle(theme), mb: 2 }}>
              Date Filters
            </Typography>

            {/* WO Creation Date */}
            <Box sx={{ mb: 2 }}>
              <Tooltip title="Defaults to last 6 months" arrow placement="left">
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    mb: 0.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  WO Creation Date
                  <InfoIcon sx={{ fontSize: "14px", opacity: 0.6 }} />
                </Typography>
              </Tooltip>
              <DateRangePicker
                value={woCreationDate}
                onChange={handleDateChange(setWoCreationDate)}
                onOpen={() => setIsDatePickerOpen(true)}
                onClose={() => setIsDatePickerOpen(false)}
                size="md"
                placeholder="Select date range"
                format="MM/dd/yyyy"
                character=" – "
                style={{ width: "100%" }}
                shouldDisableDate={(date: any, selectedIndex: any) => {
                  if (selectedIndex === 1 && woCreationDate?.[0]) {
                    return date.getTime() < woCreationDate[0]!.getTime();
                  }
                  return false;
                }}
              />
            </Box>

            {/* Scheduled Date */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  mb: 0.75,
                }}
              >
                Scheduled Date
              </Typography>
              <DateRangePicker
                value={scheduledDate}
                onChange={handleDateChange(setScheduledDate)}
                onOpen={() => setIsDatePickerOpen(true)}
                onClose={() => setIsDatePickerOpen(false)}
                size="md"
                placeholder="Select date range"
                format="MM/dd/yyyy"
                character=" – "
                style={{ width: "100%" }}
                shouldDisableDate={(date: any, selectedIndex: any) => {
                  if (selectedIndex === 1 && scheduledDate?.[0]) {
                    return date.getTime() < scheduledDate[0]!.getTime();
                  }
                  return false;
                }}
              />
            </Box>

            {/* Pickup Date */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  mb: 0.75,
                }}
              >
                Pickup Date
              </Typography>
              <DateRangePicker
                size="md"
                value={woPickupDate}
                placeholder="Select date range"
                onChange={handleDateChange(setWoPickupDate)}
                onOpen={() => setIsDatePickerOpen(true)}
                onClose={() => setIsDatePickerOpen(false)}
                format="MM/dd/yyyy"
                character=" – "
                style={{ width: "100%" }}
                shouldDisableDate={(date: any, selectedIndex: any) => {
                  if (selectedIndex === 1 && woPickupDate?.[0]) {
                    return date.getTime() < woPickupDate[0]!.getTime();
                  }
                  return false;
                }}
              />
            </Box>

            {/* Delivery Date */}
            <Box sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  mb: 0.75,
                }}
              >
                Delivery Date
              </Typography>
              <DateRangePicker
                size="md"
                value={woDeliveryDate}
                placeholder="Select date range"
                onChange={handleDateChange(setWoDeliveryDate)}
                onOpen={() => setIsDatePickerOpen(true)}
                onClose={() => setIsDatePickerOpen(false)}
                format="MM/dd/yyyy"
                character=" – "
                style={{ width: "100%" }}
                shouldDisableDate={(date: any, selectedIndex: any) => {
                  if (selectedIndex === 1 && woDeliveryDate?.[0]) {
                    return date.getTime() < woDeliveryDate[0]!.getTime();
                  }
                  return false;
                }}
              />
            </Box>
          </Box>

          {/* Options Section */}
          <Box
            sx={{
              ...drawerSectionStyle(theme),
              borderTop: `1px solid ${theme.palette.divider}`,
              mt: 1,
              pt: 2,
            }}
          >
            <Typography sx={{ ...drawerLabelStyle(theme), mb: 1.5 }}>
              Options
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeFinalizedFilter}
                  onChange={includeFinalizedChangeHandler}
                  size="small"
                  sx={{
                    "&.Mui-checked": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: theme.palette.text.primary,
                  }}
                >
                  Include Finalized
                </Typography>
              }
              sx={{
                ml: 0,
                py: 1,
                px: 1.5,
                borderRadius: "10px",
                width: "100%",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            />
          </Box>
        </Box>
      </Drawer>

      {/* Work Order dialog */}
      {showDialog && (
        <WorkOrderDialogContainer
          showHideWoPopup={showHideWorkOrderPopup}
          open={true}
        />
      )}
    </Paper>
  );
};

export default FilterPanel;
